import React, { useContext, useState, useEffect, useCallback } from "react";
import { Box, Collapse, Tooltip, Typography, useMediaQuery, useTheme } from "@mui/material";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import { Link as RouterLink, useLocation } from "react-router-dom";
import HeadsetMicRoundedIcon from "@mui/icons-material/HeadsetMicRounded";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import LocalOfferRoundedIcon from "@mui/icons-material/LocalOfferRounded";
import FavoriteRoundedIcon from "@mui/icons-material/FavoriteRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import CardGiftcardRoundedIcon from "@mui/icons-material/CardGiftcardRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import SupportAgentRoundedIcon from "@mui/icons-material/SupportAgentRounded";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import { gql, useQuery } from "@apollo/client";
import { activePromotionsForCustomer, myLoyaltyPoints } from "../../apollo/server";
import UserContext from "../../context/User";
import { useLocationContext } from "../../context/Location";
import { useOrderMode, ORDER_MODES } from "../../context/OrderMode";
import { useNavItems } from "./navItems";
import { BUTTON_RESET, C, FOCUS_RING, FONT_BODY } from "../../utils/designTokens";

const GET_DEALS_COUNT = gql`${activePromotionsForCustomer}`;
const MY_LOYALTY = gql`${myLoyaltyPoints}`;

export const SIDEBAR_WIDTH = 220;
export const SIDEBAR_WIDTH_COLLAPSED = 72;

const SIDEBAR_PREF_KEY = "sidebarCollapsed";
const SIDEBAR_PREF_EVENT = "sidebar-collapse-change";

/**
 * Shared collapsed-state hook so SideNav and MainLayout stay in sync.
 * The user's choice (via the < / > toggle) is persisted in localStorage and
 * takes priority. When the user hasn't chosen, fall back to the screen-size
 * default: expanded on lg+, icons-only on md.
 */
export function useSidebarCollapsed() {
  const theme = useTheme();
  const isLg = useMediaQuery(theme.breakpoints.up("lg"));

  const readPref = useCallback(() => {
    try {
      const v = localStorage.getItem(SIDEBAR_PREF_KEY);
      if (v === "1") return true;
      if (v === "0") return false;
    } catch (_) { /* ignore */ }
    return null; // no explicit choice
  }, []);

  const [pref, setPref] = useState(readPref);

  useEffect(() => {
    const sync = () => setPref(readPref());
    window.addEventListener(SIDEBAR_PREF_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(SIDEBAR_PREF_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, [readPref]);

  const collapsed = pref === null ? !isLg : pref;

  const toggle = useCallback(() => {
    setPref((prev) => {
      const current = prev === null ? !isLg : prev;
      const next = !current;
      try { localStorage.setItem(SIDEBAR_PREF_KEY, next ? "1" : "0"); } catch (_) { /* ignore */ }
      window.dispatchEvent(new Event(SIDEBAR_PREF_EVENT));
      return next;
    });
  }, [isLg]);

  return { collapsed, toggle };
}

function SideNav() {
  const theme = useTheme();
  const location = useLocation();
  const { isLoggedIn } = useContext(UserContext);
  const navItems = useNavItems();
  const { location: userLocation } = useLocationContext();
  const { orderMode } = useOrderMode();

  // Same variables as the Deals page, so the badge counts exactly what it lists:
  // offers from restaurants in this address's results, for the chosen mode.
  const { data: dealsData } = useQuery(GET_DEALS_COUNT, {
    variables: {
      latitude: userLocation?.latitude || null,
      longitude: userLocation?.longitude || null,
      pickup: orderMode === ORDER_MODES.PICKUP,
    },
    fetchPolicy: 'cache-first',
  });
  const { data: loyaltyData } = useQuery(MY_LOYALTY, { skip: !isLoggedIn, fetchPolicy: 'cache-and-network' });
  const loyaltyBalance = loyaltyData?.myLoyaltyPoints?.balance ?? 0;

  // Auto-expand rewards dropdown if on a rewards-related path
  const isOnRewardsPath = location.pathname.startsWith('/rewards') || location.pathname.startsWith('/my-coupons');
  const [rewardsOpen, setRewardsOpen] = useState(isOnRewardsPath);

  const dealsCount = (dealsData?.activePromotionsForCustomer || []).length;

  // Hide entirely on small screens. On md+, the collapsed state is driven by
  // the user's < / > toggle (persisted), falling back to screen size.
  const isMd = useMediaQuery(theme.breakpoints.up("md"));
  const { collapsed, toggle } = useSidebarCollapsed();

  if (!isMd) return null;

  const width = collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH;

  // 44px rows, 12px corners, a light hover; the current page in red on red tint.
  const rowSx = (active) => ({
    ...FOCUS_RING,
    display: "flex",
    alignItems: "center",
    gap: collapsed ? 0 : 1.5,
    justifyContent: collapsed ? "center" : "flex-start",
    minHeight: 44,
    boxSizing: "border-box",
    px: collapsed ? 0 : 1.5,
    mx: collapsed ? "auto" : 1,
    my: "2px",
    width: collapsed ? 44 : "auto",
    borderRadius: "12px",
    textDecoration: "none",
    cursor: "pointer",
    bgcolor: active ? C.redTint : "transparent",
    transition: "background-color .15s ease",
    "&:hover": { bgcolor: active ? C.redTint : C.surface },
  });
  const iconSx = (active, size = 22) => ({ fontSize: size, color: active ? C.red : C.muted, flexShrink: 0 });
  const labelSx = (active) => ({
    fontFamily: FONT_BODY,
    fontSize: 14,
    fontWeight: active ? 700 : 500,
    color: active ? C.red : C.ink,
    lineHeight: 1.2,
    flex: 1,
    minWidth: 0,
  });
  const pillSx = (bg, color) => ({
    flexShrink: 0,
    minWidth: 20,
    height: 20,
    px: 0.75,
    boxSizing: "border-box",
    borderRadius: "999px",
    bgcolor: bg,
    color,
    fontFamily: FONT_BODY,
    fontSize: 12,
    fontWeight: 700,
    lineHeight: "20px",
    textAlign: "center",
  });

  const renderItem = ({ label, icon: Icon, path, children, requiresAuth, getHelp, alsoOn }) => {
    if (requiresAuth && !isLoggedIn) return null;

    // Get Help — a red button row at the bottom of the list.
    if (getHelp) {
      return (
        <Box key="get-help" sx={{ mx: collapsed ? 0 : 1, mt: 1, pt: 1.5, borderTop: `1px solid ${C.border}` }}>
          {collapsed ? (
            <Tooltip title="Get Help" placement="right">
              <Box component={RouterLink} to={path} aria-label="Get Help" sx={{ ...rowSx(false), bgcolor: C.redTint, "&:hover": { bgcolor: C.redSoft } }}>
                <Icon sx={{ fontSize: 20, color: C.red }} />
              </Box>
            </Tooltip>
          ) : (
            <Box component={RouterLink} to={path} sx={{ ...rowSx(false), mx: 0, bgcolor: C.red, "&:hover": { bgcolor: C.redHover } }}>
              <Icon sx={{ fontSize: 20, color: "#FFFFFF", flexShrink: 0 }} />
              <Typography sx={{ ...labelSx(false), color: "#FFFFFF", fontWeight: 700 }}>Get Help</Typography>
            </Box>
          )}
        </Box>
      );
    }

    const isActive = path === "/" ? location.pathname === "/" : [path, ...(alsoOn || [])].some((p) => location.pathname.startsWith(p));
    const hasChildren = children && children.length > 0;
    const isOpen = hasChildren && rewardsOpen;
    const badge =
      label === "Deals" && dealsCount > 0 ? (
        <Box component="span" sx={pillSx(C.red, "#FFFFFF")}>{dealsCount}</Box>
      ) : hasChildren && loyaltyBalance > 0 ? (
        <Box component="span" sx={pillSx(C.goldSoft, C.gold)}>{loyaltyBalance.toLocaleString()}</Box>
      ) : null;

    const row = collapsed ? (
      <Tooltip title={label} placement="right">
        <Box component={RouterLink} to={path} aria-label={label} aria-current={isActive ? "page" : undefined} sx={rowSx(isActive)}>
          <Icon sx={iconSx(isActive)} />
        </Box>
      </Tooltip>
    ) : hasChildren ? (
      // My Rewards: the label goes to the page, the chevron shows its links.
      <Box sx={{ display: "flex", alignItems: "center", mx: 1, my: "2px", gap: 0.25 }}>
        <Box component={RouterLink} to={path} aria-current={isActive ? "page" : undefined} sx={{ ...rowSx(isActive), mx: 0, my: 0, flex: 1, minWidth: 0, pr: 1 }}>
          <Icon sx={iconSx(isActive)} />
          <Typography sx={labelSx(isActive)}>{label}</Typography>
          {badge}
        </Box>
        <Box
          component="button"
          type="button"
          onClick={() => setRewardsOpen((o) => !o)}
          aria-expanded={isOpen}
          aria-label={isOpen ? `Hide ${label} links` : `Show ${label} links`}
          sx={{ ...BUTTON_RESET, ...FOCUS_RING, width: 32, height: 44, flexShrink: 0, borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center", color: C.muted, "&:hover": { bgcolor: C.surface } }}
        >
          {isOpen ? <ExpandLessRoundedIcon sx={{ fontSize: 20 }} /> : <ExpandMoreRoundedIcon sx={{ fontSize: 20 }} />}
        </Box>
      </Box>
    ) : (
      <Box component={RouterLink} to={path} aria-current={isActive ? "page" : undefined} sx={rowSx(isActive)}>
        <Icon sx={iconSx(isActive)} />
        <Typography sx={labelSx(isActive)}>{label}</Typography>
        {badge}
      </Box>
    );

    return (
      <Box key={path}>
        {row}

        {/* Sub-items: under the row when expanded, as their own icons when collapsed. */}
        {hasChildren && !collapsed && (
          <Collapse in={isOpen}>
            {children.map((child) => {
              const childActive = location.pathname.startsWith(child.path);
              const ChildIcon = child.icon;
              return (
                <Box key={child.path} component={RouterLink} to={child.path} aria-current={childActive ? "page" : undefined} sx={{ ...rowSx(childActive), pl: 4.5, minHeight: 40 }}>
                  <ChildIcon sx={iconSx(childActive, 18)} />
                  <Typography sx={{ ...labelSx(childActive), fontSize: 13 }}>{child.label}</Typography>
                </Box>
              );
            })}
          </Collapse>
        )}
        {hasChildren && collapsed && children.map((child) => {
          const childActive = location.pathname.startsWith(child.path);
          const ChildIcon = child.icon;
          return (
            <Tooltip key={child.path} title={child.label} placement="right">
              <Box component={RouterLink} to={child.path} aria-label={child.label} aria-current={childActive ? "page" : undefined} sx={rowSx(childActive)}>
                <ChildIcon sx={iconSx(childActive, 20)} />
              </Box>
            </Tooltip>
          );
        })}
      </Box>
    );
  };

  return (
    <Box
      component="nav"
      aria-label="Main"
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        height: "100vh",
        width,
        bgcolor: C.bg,
        borderRight: `1px solid ${C.border}`,
        display: "flex",
        flexDirection: "column",
        pt: "64px",
        zIndex: 1100,
        transition: "width 0.2s ease",
        overflowX: "hidden",
        overflowY: "hidden",
      }}
    >
      {/* Collapse / expand toggle — a clear row at the top of the sidebar */}
      <Tooltip title={collapsed ? "Expand menu" : "Collapse menu"} placement="right">
        <Box
          component="button"
          type="button"
          onClick={toggle}
          aria-label={collapsed ? "Expand menu" : "Collapse menu"}
          aria-expanded={!collapsed}
          sx={{
            ...BUTTON_RESET,
            ...FOCUS_RING,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.75,
            boxSizing: "border-box",
            mx: collapsed ? "auto" : 1,
            mt: 1.5,
            mb: 1,
            height: 40,
            width: collapsed ? 44 : "calc(100% - 16px)",
            borderRadius: "12px",
            color: C.muted,
            border: `1px solid ${C.border}`,
            bgcolor: C.bg,
            fontFamily: FONT_BODY,
            transition: "background-color .15s ease, color .15s ease",
            "&:hover": { bgcolor: C.surface, color: C.ink },
          }}
        >
          {collapsed ? (
            <ChevronRightRoundedIcon sx={{ fontSize: 22 }} />
          ) : (
            <>
              <Box component="span" sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1 }}>
                Collapse
              </Box>
              <ChevronLeftRoundedIcon sx={{ fontSize: 20 }} />
            </>
          )}
        </Box>
      </Tooltip>

      <Box sx={{ flex: 1, pt: 0, pb: 2, overflowY: "auto", minHeight: 0 }}>
        {navItems.map(renderItem)}
      </Box>

    </Box>
  );
}

export default SideNav;
